import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CandidateForm from "@/components/candidates/CandidateForm";
import FileManager from "@/components/files/FileManager";
import { CandidateWorkflow, ResumeVersions, CompensationPanel, NC } from "@/components/candidates/CandidatePanels";
import { StatusBadge } from "@/components/shared";
import AIMarkdown from "@/components/shared/AIMarkdown";
import { initials, money, fmtDate, timeAgo, label } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Upload, Sparkles, FileText, Download, Archive, Loader2, MapPin, Mail, Phone, Briefcase, Cloud, ExternalLink,
} from "lucide-react";

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [activity, setActivity] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveMappings, setDriveMappings] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}`);
      setC(data);
      setAiSummary(data.ai_summary);
    } catch (e) { toast.error(apiErr(e)); navigate("/candidates"); }
  }, [id, navigate]);

  const loadActivity = useCallback(async () => {
    try { const { data } = await api.get(`/candidates/${id}/activity`); setActivity(data.items || []); } catch {}
  }, [id]);

  useEffect(() => { load(); loadActivity(); }, [load, loadActivity]);

  const loadDrive = useCallback(async () => {
    try {
      const st = await api.get("/nexreq-drive/status");
      setDriveStatus(st.data);
      if (st.data?.connected) {
        const m = await api.get(`/nexreq-drive/mappings`, {
          params: { record_type: "candidate", record_id: id, limit: 100 },
        });
        setDriveMappings(m.data.items || []);
      }
    } catch {}
  }, [id]);

  useEffect(() => { loadDrive(); }, [loadDrive]);

  const backfillDrive = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.post(`/nexreq-drive/mirror/candidate/${id}`, {});
      toast.success(
        `Mirrored ${data.mirrored} · already there ${data.skipped_already_mirrored} · failed ${data.failed}`
      );
      loadDrive();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDriveBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/candidates/${id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load(); loadActivity();
      // Refresh Drive mapping list shortly (background mirror completes async)
      setTimeout(loadDrive, 2500);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const aiSummarize = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/candidates/${id}/ai-summary`, null, { params: { save: true } });
      setAiSummary(data.summary);
      toast.success("AI summary generated");
      loadActivity();
    } catch (e) { toast.error(apiErr(e, "AI unavailable")); }
    finally { setAiBusy(false); }
  };

  const aiParse = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/candidates/${id}/parse-resume`);
      setParsed(data.parsed);
      toast.success("Resume parsed — review below");
    } catch (e) { toast.error(apiErr(e, "Parsing failed")); }
    finally { setAiBusy(false); }
  };

  const doArchive = async () => {
    try {
      await api.post(`/candidates/${id}/archive`, { reason: archiveReason });
      toast.success("Candidate archived");
      setArchiveOpen(false); navigate("/candidates");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!c) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  const TABS = [
    ["overview", "Overview"], ["workflow", "Workflow & Screening"], ["compensation", "Compensation"],
    ["resume", "Resume"], ["documents", "Documents"], ["experience", "Experience"],
    ["education", "Education"], ["skills", "Skills"], ["ai", "AI Insights"], ["activity", "Activity"],
  ];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/candidates")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="candidate-back">
        <ArrowLeft className="h-4 w-4" /> Back to Candidates
      </button>

      {/* Header */}
      <Card className="p-6" data-testid="candidate-profile-header">
        <div className="flex flex-wrap items-start gap-5">
          <Avatar className="h-20 w-20"><AvatarFallback className="bg-[#255058] text-white text-2xl font-display">{initials(c.full_name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight">{c.full_name}</h1>
              <StatusBadge value={c.status} />
            </div>
            <div className="text-muted-foreground mt-1">{c.current_title || "—"}{c.current_employer ? ` · ${c.current_employer}` : ""}</div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
              {c.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{c.location}</span>}
              {c.primary_email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{c.primary_email}</span>}
              {c.phones?.[0] && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phones[0]}</span>}
              {c.owner_name && <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{c.owner_name}</span>}
            </div>
            <div className="mt-4 max-w-xs">
              <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Profile completeness</span><span className="font-semibold">{c.profile_completeness || 0}%</span></div>
              <Progress value={c.profile_completeness || 0} className="h-2" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {can("candidate:edit") && <Button size="sm" onClick={() => setEditOpen(true)} data-testid="candidate-edit-button" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Pencil className="h-4 w-4" /> Edit</Button>}
            {can("candidate:archive") && <Button size="sm" variant="outline" onClick={() => setArchiveOpen(true)} data-testid="candidate-archive-button" className="gap-1.5 text-red-600"><Archive className="h-4 w-4" /> Archive</Button>}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`candidate-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="font-display font-semibold mb-3">Details</div>
              <dl className="space-y-2 text-sm">
                <Row k="Region" v={c.region} />
                <Row k="Work Authorization" v={c.work_authorization} />
                <Row k="Notice Period" v={c.notice_period} />
                <Row k="Years Experience" v={c.years_experience} />
                <Row k="Work Model" v={c.work_model} />
                <Row k="Source" v={c.source} />
                <Row k="Consent" v={c.consent_status} />
                <Row k="Availability" v={c.availability} />
                <Row k="Interview Availability" v={c.interview_availability} />
                <Row k="Time Zone" v={c.time_zone} />
              </dl>
            </Card>
            <Card className="p-5">
              <div className="font-display font-semibold mb-3">Compensation</div>
              {("current_compensation" in c) ? (
                <dl className="space-y-2 text-sm">
                  <Row k="Current" v={money(c.current_compensation, c.currency)} />
                  <Row k="Expected" v={money(c.expected_compensation, c.currency)} />
                  <Row k="Frequency" v={c.compensation_frequency} />
                </dl>
              ) : <div className="text-sm text-muted-foreground">Restricted — you don't have permission to view compensation.</div>}
              {c.screening_notes && <div className="mt-4 pt-4 border-t border-border"><div className="text-xs font-semibold text-muted-foreground mb-1">Screening Notes</div><p className="text-sm">{c.screening_notes}</p></div>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="workflow" className="mt-4">
          <CandidateWorkflow candidate={c} onChanged={() => { load(); loadActivity(); }} />
        </TabsContent>

        <TabsContent value="compensation" className="mt-4">
          <CompensationPanel candidateId={id} canEdit={can("candidate:edit")} />
        </TabsContent>

        <TabsContent value="resume" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-semibold">Resume</div>
              {can("candidate:edit") && (
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.doc,.docx,.txt" hidden data-testid="resume-upload-input"
                    onChange={(e) => upload(e.target.files?.[0])} />
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-[#255058] text-white hover:bg-[#1c3d43]">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                  </span>
                </label>
              )}
            </div>
            {c.resume ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border">
                <FileText className="h-8 w-8 text-[#255058]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.resume.filename}</div>
                  <div className="text-xs text-muted-foreground">Version {c.resume.version} · {fmtDate(c.resume.uploaded_at)}</div>
                </div>
                <a href={`${API}/candidates/${id}/resume/download`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="gap-1.5"><Download className="h-4 w-4" /> View</Button>
                </a>
              </div>
            ) : <div className="text-sm text-muted-foreground">No resume uploaded. Upload a PDF, DOC or DOCX.</div>}
            <ResumeVersions candidateId={id} />

            {driveStatus?.connected && (
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Cloud className="h-4 w-4 text-[#255058]" />
                    NEXREQ Drive mirror
                  </div>
                  {can("candidate:edit") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={backfillDrive}
                      disabled={driveBusy}
                      data-testid="drive-backfill-btn"
                    >
                      {driveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Cloud className="h-3.5 w-3.5 mr-1" />}
                      Sync all documents to Drive
                    </Button>
                  )}
                </div>
                {driveMappings.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    No files mirrored to Drive yet. New uploads are mirrored automatically; click "Sync all" to backfill existing files.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {driveMappings.map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-xs border rounded px-2 py-1">
                        <div className="truncate">
                          <span className="font-medium">{m.file_name}</span>
                          <span className="text-muted-foreground ml-2">· {m.category}</span>
                        </div>
                        {m.web_view_link && (
                          <a href={m.web_view_link} target="_blank" rel="noreferrer" className="text-[#255058] hover:underline flex items-center gap-1">
                            Open <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {c.resume && can("ai:access") && (
              <div className="mt-4">
                <Button size="sm" variant="outline" onClick={aiParse} disabled={aiBusy} data-testid="ai-parse-button" className="gap-1.5">
                  <Sparkles className="h-4 w-4 text-[#255058]" /> Parse with AI
                </Button>
                {parsed && (
                  <pre className="mt-3 p-3 rounded-lg bg-muted text-xs overflow-auto max-h-72 scrollbar-thin">{JSON.stringify(parsed, null, 2)}</pre>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <FileManager recordType="candidate" recordId={id} canWrite={can("candidate:edit")} title="Candidate Documents" />
        </TabsContent>

        <TabsContent value="experience" className="mt-4">
          <Card className="p-5">
            {(c.employment || []).length ? (
              <div className="space-y-4">
                {c.employment.map((e, i) => (
                  <div key={i} className="border-l-2 border-[#255058]/30 pl-4">
                    <div className="font-medium">{e.title}</div>
                    <div className="text-sm text-muted-foreground">{e.company} · {e.start} – {e.end}</div>
                    {e.summary && <p className="text-sm mt-1">{e.summary}</p>}
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-muted-foreground">No employment history recorded.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="education" className="mt-4">
          <Card className="p-5">
            {(c.education || []).length ? c.education.map((e, i) => (
              <div key={i} className="py-1"><span className="font-medium">{e.degree}</span> <span className="text-muted-foreground">— {e.institution} {e.year}</span></div>
            )) : <div className="text-sm text-muted-foreground">No education recorded.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <Card className="p-5">
            <div className="flex flex-wrap gap-2">
              {(c.skills || []).length ? c.skills.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)
                : <span className="text-sm text-muted-foreground">No skills recorded.</span>}
            </div>
            {(c.certifications || []).length > 0 && (
              <div className="mt-4"><div className="text-xs font-semibold text-muted-foreground mb-2">Certifications</div>
                <div className="flex flex-wrap gap-2">{c.certifications.map((s) => <Badge key={s} className="status-purple border-0">{s}</Badge>)}</div></div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-display font-semibold"><Sparkles className="h-4 w-4 text-[#255058]" /> NEXREQ Intelligence</div>
              {can("ai:access") && <Button size="sm" onClick={aiSummarize} disabled={aiBusy} data-testid="ai-summary-button" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Summary</Button>}
            </div>
            {aiSummary ? <AIMarkdown text={aiSummary} testId="candidate-ai-summary" />
              : <div className="text-sm text-muted-foreground">No AI summary yet. Generate one from the candidate's profile and resume. AI never edits records without your confirmation.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="p-5">
            <div className="space-y-3">
              {activity.length ? activity.map((a) => (
                <div key={a.id} className="flex gap-3 text-sm">
                  <div className="h-1.5 w-1.5 rounded-full bg-[#255058] mt-1.5 shrink-0" />
                  <div><div>{a.summary}</div><div className="text-[11px] text-muted-foreground">{a.actor_name} · {timeAgo(a.created_at)}</div></div>
                </div>
              )) : <div className="text-sm text-muted-foreground">No activity yet.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <CandidateForm open={editOpen} candidate={c} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); loadActivity(); }} />

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Archive Candidate</DialogTitle><DialogDescription>Archiving is reversible. Provide a reason for the audit trail.</DialogDescription></DialogHeader>
          <Textarea placeholder="Reason for archiving…" value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} data-testid="archive-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doArchive} data-testid="archive-confirm-button">Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt>
    <dd className={`text-right font-medium ${v || v === 0 ? "" : "italic text-muted-foreground"}`}>{v || v === 0 ? v : NC(v)}</dd></div>;
}
